/**
 * Unified auth module — wraps shared/customer-ui/auth/auth-state.js
 * Bridges shared auth state into workspace Store + EventBus
 */

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { CONFIG } from '../../core/config.js'
import { createLogger } from '../../utils/logger.js'
import { fetchWithTimeout } from '../../api/index.js'
import {
  refreshCreatorHubCanonicalSession,
  logoutCreatorHubCanonicalSession,
} from './canonical-client.js'

const log = createLogger('auth')
// Shared auth storage keys (same as shared/customer-ui/auth/auth-state.js)
const STORAGE_KEYS = {
  TOKEN: 'mms_customer_token',
  CUSTOMER: 'mms_customer',
  SESSION_ID: 'mms_session_id',
}
const REFRESH_KEY = 'mms_customer_refresh_token'

let authAttemptVersion = 0

/** Start an auth validation attempt that supersedes every older attempt. */
export function beginAuthAttempt() {
  authAttemptVersion += 1
  return authAttemptVersion
}

/** Check whether an async auth validation is still allowed to commit. */
export function isAuthAttemptCurrent(attempt) {
  return attempt === authAttemptVersion
}

/**
 * Validate a bearer against the canonical customer realm. Returns the
 * customer row only for realm/audience === 'customer' (an admin-realm token
 * must never authorize a customer session).
 */
async function fetchCustomerSession(customerApiBase, token) {
  const res = await fetchWithTimeout(`${customerApiBase}/api/v1/auth/session`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const data = await res.json()
  const session = data?.data
  if (!res.ok || !data?.success) return { ok: false, status: res.status }
  if (session?.realm !== 'customer' || session?.audience !== 'customer' || !session?.customer)
    return { ok: false, status: res.status }
  return { ok: true, customer: session.customer }
}

/**
 * Initialize auth: load stored token, validate, sync to store.
 * Validation goes straight to canonical /api/v1/auth/session (the legacy
 * /api/auth/me chain is retired). A 401 with a surviving refresh token
 * rotates once via the refresh grant and re-validates — the token lifecycle
 * is driven by the client, not by silent 24 h expiry (G4).
 */
export async function initAuth() {
  // Ensure session ID exists
  let sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID)
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).slice(2, 26)
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId)
  }

  // Check for stored auth
  const token = store.getState('auth.token') || localStorage.getItem(STORAGE_KEYS.TOKEN)
  if (!token) {
    log.info('No stored auth')
    return
  }

  const attempt = beginAuthAttempt()
  const customerApiBase = CONFIG.CUSTOMER_API || 'https://customer-api.mamamiya.store'

  try {
    let session = await fetchCustomerSession(customerApiBase, token)
    if (!isAuthAttemptCurrent(attempt)) return

    // Access token rejected but a refresh token survives → rotate once,
    // then re-validate with the fresh access token.
    if (!session.ok && session.status === 401 && localStorage.getItem(REFRESH_KEY)) {
      const rotated = await refreshCreatorHubCanonicalSession()
      if (!isAuthAttemptCurrent(attempt)) return
      if (rotated) {
        const rotatedToken = localStorage.getItem(STORAGE_KEYS.TOKEN)
        session = await fetchCustomerSession(customerApiBase, rotatedToken)
        if (!isAuthAttemptCurrent(attempt)) return
        if (session.ok) {
          saveAuth(session.customer, rotatedToken)
          log.info('Auth rotated + restored:', session.customer.email || session.customer.id)
          return
        }
      }
    }

    if (session.ok) {
      saveAuth(session.customer, token)
      log.info('Auth restored:', session.customer.email || session.customer.id)
    } else {
      clearAuth()
      log.info('Stored token invalid, cleared')
    }
  } catch (err) {
    if (!isAuthAttemptCurrent(attempt)) return
    // A cached customer is not proof that this bearer token is still valid.
    // Fail closed during outages instead of authorizing writes as a stale user.
    clearAuth()
    log.warn('Auth validation unavailable; session cleared:', err?.message || err)
  }
}

/**
 * Save auth to localStorage + store + emit events
 */
export function saveAuth(customer, token) {
  try {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token)
    localStorage.setItem(STORAGE_KEYS.CUSTOMER, JSON.stringify(customer))
  } catch (err) {
    // Embedded/privacy-restricted contexts may deny persistence; a validated
    // session must remain usable in memory without leaving a partial cache.
    try {
      localStorage.removeItem(STORAGE_KEYS.TOKEN)
      localStorage.removeItem(STORAGE_KEYS.CUSTOMER)
    } catch {
      /* storage is unavailable */
    }
    log.warn('Auth persistence unavailable; using in-memory session:', err?.message || err)
  }

  store.setState('auth', { token, customer, showLoginModal: false })
  eventBus.emit(EVENTS.USER_LOGIN, { customer, token })
  eventBus.emit(EVENTS.AUTH_CHANGED, { isAuthenticated: true, user: customer })

  // Cross-tab sync via storage event
  window.dispatchEvent(
    new CustomEvent('authStateChanged', {
      detail: { isAuthenticated: true, user: customer },
    })
  )
}

/**
 * Clear auth
 */
export function clearAuth() {
  try {
    localStorage.removeItem(STORAGE_KEYS.TOKEN)
    localStorage.removeItem(STORAGE_KEYS.CUSTOMER)
  } catch (err) {
    log.warn('Auth persistence unavailable while clearing session:', err?.message || err)
  }

  store.setState('auth', { token: null, customer: null, showLoginModal: false })
  eventBus.emit(EVENTS.USER_LOGOUT)
  eventBus.emit(EVENTS.AUTH_CHANGED, { isAuthenticated: false, user: null })

  window.dispatchEvent(
    new CustomEvent('authStateChanged', {
      detail: { isAuthenticated: false, user: null },
    })
  )
}

/**
 * Get current auth token
 */
export function getToken() {
  return store.getState('auth.token') || localStorage.getItem(STORAGE_KEYS.TOKEN)
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!getToken()
}

/**
 * Require auth — returns customer or shows login modal
 */
export function requireAuth() {
  const token = getToken()
  const customer = store.getState('auth.customer')
  if (token && customer) return customer

  store.setState('auth.showLoginModal', true)
  return null
}

/**
 * Sign out: revoke every refresh token for this principal on the canonical
 * realm, then clear local state. Live caller: Me page sign-out button.
 */
export async function signOut(fetcher = fetch) {
  await logoutCreatorHubCanonicalSession(fetcher).catch(() => {})
  clearAuth()
}

import { handleCreatorHubAuthCallback } from './canonical-callback.js'

/**
 * /auth/callback boot path. The exchange is AWAITED but bounded (every leg
 * is timeout-wrapped in canonical-client), and the caller MUST NOT return
 * early after this — the app mounts whether the exchange succeeded or not
 * (bao 2026-10-24: post-Google-login page stuck on the pre-mount placeholder
 * forever, because boot returned here and nothing ever mounted).
 * @returns {Promise<boolean>} true when this boot IS the callback path.
 */
export async function initCanonicalAuthCallback() {
  if (window.location.pathname !== '/auth/callback') return false
  // Visible progress in the pre-mount placeholder — a callback boot is never
  // a silent wall.
  const el = document.querySelector('#mode-create .pf-empty__msg')
  if (el && !el.dataset.bootErr) el.textContent = 'Signing you in…'
  const result = await handleCreatorHubAuthCallback()
  if (result.ok) {
    window.history.replaceState(null, '', '/')
  } else {
    eventBus.emit(EVENTS.TOAST_SHOW, {
      type: 'error',
      message: 'Sign-in could not be completed — please try again',
    })
  }
  return true
}
